import React, { useState } from "react";
import {
    ScreeningMatch,
    ScreeningProps,
} from "../../../../types/cdd-records.types";
import {
    formatDateShort,
    formatDateTime,
} from "../../../../utils/dateFormatters";
import {
    getScreeningStatusBadge,
    screeningTypeLabels,
} from "./screeningConfig";

export const Screening: React.FC<ScreeningProps> = ({
  results,
  onResolveMatch,
  onInitiateScreening,
  recordVerificationStatus,
}) => {
  const isRecordVerified = recordVerificationStatus === "verified";
  const [expandedResult, setExpandedResult] = useState<string | null>(null);

  // When the record is verified, potential matches are treated as reviewed & cleared
  const getEffectiveStatus = (status: string) =>
    isRecordVerified && status === "potential-match" ? "reviewed-cleared" : status;

  // Calculate summary stats
  const clearScreenings = results.filter((r) => r.status === "clear").length;
  const reviewedAndCleared = isRecordVerified
    ? results.filter((r) => r.status === "potential-match").length
    : 0;
  const potentialMatches = isRecordVerified
    ? 0
    : results.filter((r) => r.status === "potential-match").length;
  const confirmedMatches = results.filter(
    (r) => r.status === "confirmed-match",
  ).length;
  const pendingReview = results
    .flatMap((r) => r.matches || [])
    .filter((m) => m.resolution === "pending").length;

  const handleResolveMatch = (
    matchId: string,
    resolution: ScreeningMatch["resolution"],
  ) => {
    if (onResolveMatch) {
      onResolveMatch(matchId, resolution);
    }
  };

  return (
    <div className="bg-gradient-to-br from-slate-50 via-white to-blue-50 rounded-xl shadow-lg border border-slate-200/60 overflow-hidden">
      {/* Header with gradient */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 border-b border-slate-200/60 bg-gradient-to-r from-slate-800 via-slate-700 to-slate-800">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white">
              Screening Results
            </h3>
          </div>
          {onInitiateScreening && !isRecordVerified && (
            <button
              onClick={onInitiateScreening}
              className="px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm font-medium rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm flex items-center gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
              New Screening
            </button>
          )}
        </div>
      </div>

      {/* Verified + potential matches banner */}
      {isRecordVerified && reviewedAndCleared > 0 && (
        <div className="px-6 py-3 bg-teal-50 border-b border-teal-200 flex items-center gap-3">
          <svg className="w-5 h-5 text-teal-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm text-teal-800">
            <span className="font-semibold">{reviewedAndCleared} potential match{reviewedAndCleared !== 1 ? "es were" : " was"} reviewed and cleared</span> as part of the verification process. No active sanctions or PEP concerns remain.
          </p>
        </div>
      )}

      {/* Summary Stats - Vibrant Gradient Cards */}
      <div className="px-4 sm:px-6 py-4 sm:py-5 bg-gradient-to-r from-slate-100/50 via-white to-slate-100/50 border-b border-slate-200/60">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Clear - Emerald to Teal */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-emerald-500 via-emerald-600 to-teal-700 rounded-2xl p-3 sm:p-5 border border-emerald-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {clearScreenings}
                  </p>
                  <p className="text-sm font-semibold text-emerald-100 mt-1">
                    Clear
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Potential Matches / Reviewed & Cleared - Amber or Teal depending on state */}
          <div className="relative group overflow-hidden rounded-2xl">
            {isRecordVerified ? (
              <>
                <div className="relative bg-gradient-to-br from-teal-500 via-teal-600 to-cyan-700 rounded-2xl p-3 sm:p-5 border border-teal-400/30 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">{reviewedAndCleared}</p>
                      <p className="text-sm font-semibold text-teal-100 mt-1">Reviewed & Cleared</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 rounded-2xl p-3 sm:p-5 border border-amber-400/30 shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">{potentialMatches}</p>
                      <p className="text-sm font-semibold text-amber-100 mt-1">Potential Matches</p>
                    </div>
                    <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                      <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
          {/* Confirmed Matches - Rose to Red */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-rose-500 via-rose-600 to-red-700 rounded-2xl p-3 sm:p-5 border border-rose-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {confirmedMatches}
                  </p>
                  <p className="text-sm font-semibold text-rose-100 mt-1">
                    Confirmed Matches
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          {/* Pending Review - Blue to Indigo */}
          <div className="relative group overflow-hidden rounded-2xl">
            <div className="relative bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-700 rounded-2xl p-3 sm:p-5 border border-blue-400/30 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl sm:text-4xl font-black text-white drop-shadow-lg">
                    {pendingReview}
                  </p>
                  <p className="text-sm font-semibold text-blue-100 mt-1">
                    Pending Review
                  </p>
                </div>
                <div className="w-8 h-8 sm:w-12 sm:h-12 bg-white/20 rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <svg
                    className="w-6 h-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Screening Results List */}
      <div className="divide-y divide-slate-200/60">
        {results.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 mb-4">
              <svg
                className="h-8 w-8 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <p className="text-slate-500 font-medium">
              No screening results available
            </p>
            <p className="text-slate-400 text-sm mt-1">
              Initiate a new screening to get started
            </p>
          </div>
        ) : (
          results.map((result: any) => {
            const typeConfig =
              screeningTypeLabels[result.screeningType] ||
              screeningTypeLabels["comprehensive"];
            const effectiveStatus = getEffectiveStatus(result.status);
            const statusBadge =
              effectiveStatus === "reviewed-cleared"
                ? {
                    class: "bg-gradient-to-r from-teal-100 to-cyan-100 text-teal-800 border-teal-300 shadow-sm",
                    label: "Reviewed & Cleared",
                  }
                : getScreeningStatusBadge(result.status as any);
            const isExpanded = expandedResult === result.id;

            return (
              <div
                key={result.id}
                className="hover:bg-gradient-to-r hover:from-slate-50/50 hover:to-transparent transition-all duration-200"
              >
                {/* Result Summary */}
                <div
                  className="px-4 sm:px-6 py-4 sm:py-5 cursor-pointer"
                  onClick={() =>
                    setExpandedResult(isExpanded ? null : result.id)
                  }
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className={`p-3 rounded-xl ${typeConfig.iconBg} shadow-sm`}
                      >
                        {typeConfig.icon}
                      </div>
                      <div>
                        <h4 className="text-sm font-semibold text-slate-800">
                          {typeConfig.label}
                        </h4>
                        <p className="text-xs text-slate-500 mt-0.5">
                          Screened:{" "}
                          <span className="font-medium text-slate-600">
                            {result.screenedEntity}
                          </span>{" "}
                          • {formatDateTime(result.screeningDate)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      {result.provider && (
                        <span className="text-xs text-slate-400 bg-slate-100 px-2 py-1 rounded-full">
                          via {result.provider}
                        </span>
                      )}
                      <span
                        className={`px-3 py-1.5 text-xs font-semibold rounded-full border ${statusBadge.class}`}
                      >
                        {statusBadge.label}
                      </span>
                      {result.matches && result.matches.length > 0 && (
                        <span className="bg-gradient-to-r from-slate-100 to-slate-200 text-slate-700 text-xs font-medium px-2.5 py-1 rounded-full">
                          {result.matches.length} match
                          {result.matches.length !== 1 ? "es" : ""}
                        </span>
                      )}
                      <svg
                        className={`w-5 h-5 text-gray-400 transform transition-transform ${isExpanded ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Expanded Details */}
                {isExpanded && (
                  <div className="px-6 pb-5 bg-gradient-to-br from-slate-50/80 via-white/50 to-slate-50/80">
                    <div className="pt-5 border-t border-slate-200/60">
                      {/* Screening Info */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
                        <div className="bg-white/80 rounded-lg p-3 border border-slate-200/40">
                          <span className="text-xs font-medium text-slate-500 block">
                            Screening Date
                          </span>
                          <span className="text-sm font-semibold text-slate-800">
                            {formatDateShort(result.screeningDate)}
                          </span>
                        </div>
                        {result.reviewedBy && (
                          <div className="bg-white/80 rounded-lg p-3 border border-slate-200/40">
                            <span className="text-xs font-medium text-slate-500 block">
                              Reviewed By
                            </span>
                            <span className="text-sm font-semibold text-slate-800">
                              {result.reviewedBy}
                            </span>
                          </div>
                        )}
                        {result.reviewedDate && (
                          <div className="bg-white/80 rounded-lg p-3 border border-slate-200/40">
                            <span className="text-xs font-medium text-slate-500 block">
                              Review Date
                            </span>
                            <span className="text-sm font-semibold text-slate-800">
                              {formatDateShort(result.reviewedDate)}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {result.notes && (
                        <div className="mb-5 p-4 bg-gradient-to-br from-sky-50/50 to-indigo-50/50 rounded-xl border border-sky-200/40">
                          <span className="text-xs font-semibold text-sky-700 block mb-1.5 uppercase tracking-wide">
                            Notes
                          </span>
                          <p className="text-sm text-slate-700">
                            {result.notes}
                          </p>
                        </div>
                      )}

                      {/* Matches */}
                      {result.matches && result.matches.length > 0 && (
                        <div>
                          <h5 className="text-sm font-semibold text-slate-800 mb-4 flex items-center gap-2">
                            <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-slate-600 to-slate-700 text-white text-xs font-bold">
                              {result.matches.length}
                            </span>
                            Matches Found
                          </h5>
                          <div className="space-y-4">
                            {result.matches.map((match: any) => (
                              <div
                                key={match.id}
                                className="bg-gradient-to-br from-white to-slate-50/50 rounded-xl border border-slate-200/60 p-5 shadow-sm hover:shadow-md transition-shadow"
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-3">
                                      <span className="font-semibold text-slate-800">
                                        {match.matchedName}
                                      </span>
                                      <span
                                        className={`px-2.5 py-1 text-xs font-semibold rounded-full ${
                                          match.matchType === "exact"
                                            ? "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 ring-1 ring-rose-200"
                                            : match.matchType === "close"
                                              ? "bg-gradient-to-r from-orange-100 to-amber-100 text-orange-800 ring-1 ring-orange-200"
                                              : "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 ring-1 ring-yellow-200"
                                        }`}
                                      >
                                        {match.matchType} match (
                                        {match.matchScore}%)
                                      </span>
                                    </div>
                                    <div className="mt-3 text-sm text-slate-600 space-y-1.5">
                                      {match.matchedList && (
                                        <p className="flex items-center gap-2">
                                          <span className="text-slate-400">
                                            List:
                                          </span>
                                          <span className="font-medium text-slate-700">
                                            {match.matchedList}
                                          </span>
                                        </p>
                                      )}
                                      {match.matchedCountry && (
                                        <p className="flex items-center gap-2">
                                          <span className="text-slate-400">
                                            Country:
                                          </span>
                                          <span className="font-medium text-slate-700">
                                            {match.matchedCountry}
                                          </span>
                                        </p>
                                      )}
                                      {match.matchDetails && (
                                        <p className="text-slate-600 italic">
                                          {match.matchDetails}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="ml-4">
                                    {match.resolution ? (
                                      <div className="text-right">
                                        <span
                                          className={`px-3 py-1.5 text-xs font-semibold rounded-full ${
                                            match.resolution === "confirmed"
                                              ? "bg-gradient-to-r from-rose-100 to-red-100 text-rose-800 ring-1 ring-rose-200"
                                              : match.resolution ===
                                                  "false-positive"
                                                ? "bg-gradient-to-r from-emerald-100 to-green-100 text-emerald-800 ring-1 ring-emerald-200"
                                                : "bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 ring-1 ring-amber-200"
                                          }`}
                                        >
                                          {match.resolution === "confirmed"
                                            ? "Confirmed"
                                            : match.resolution ===
                                                "false-positive"
                                              ? "False Positive"
                                              : "Pending"}
                                        </span>
                                        {match.resolvedBy && (
                                          <p className="text-xs text-slate-500 mt-1.5">
                                            by {match.resolvedBy}
                                          </p>
                                        )}
                                      </div>
                                    ) : (
                                      <div className="flex gap-2">
                                        <button
                                          onClick={() =>
                                            handleResolveMatch(
                                              match.id,
                                              "false-positive",
                                            )
                                          }
                                          className="px-4 py-2 text-xs font-semibold bg-gradient-to-r from-emerald-500 to-green-600 text-white rounded-lg hover:from-emerald-600 hover:to-green-700 transition-all shadow-md shadow-emerald-500/25"
                                        >
                                          False Positive
                                        </button>
                                        <button
                                          onClick={() =>
                                            handleResolveMatch(
                                              match.id,
                                              "confirmed",
                                            )
                                          }
                                          className="px-4 py-2 text-xs font-semibold bg-gradient-to-r from-rose-500 to-red-600 text-white rounded-lg hover:from-rose-600 hover:to-red-700 transition-all shadow-md shadow-rose-500/25"
                                        >
                                          Confirm
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {match.resolutionNotes && (
                                  <div className="mt-4 pt-4 border-t border-slate-100">
                                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                                      Resolution Notes:
                                    </span>
                                    <p className="text-sm text-slate-700 mt-1">
                                      {match.resolutionNotes}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Screening;
